import { html, render, type TemplateResult } from 'lit-html';

/** The five portal role levels, lowest first (level = index). */
export const PK_ROLES: ReadonlyArray<{ level: number; name: string; label: string }> = [
    { level: 0, name: 'reader',   label: 'Reader' },
    { level: 1, name: 'auditor',  label: 'Auditor' },
    { level: 2, name: 'member',   label: 'Member' },
    { level: 3, name: 'operator', label: 'Operator' },
    { level: 4, name: 'owner',    label: 'Owner' },
];

/** A role level from a number (`"0"`–`"4"`) or a name (`reader`..`owner`, case-insensitive); null when unknown. */
export function parseRoleLevel(value: string | null | undefined): number | null {
    if (value === null || value === undefined) return null;
    const v = value.trim().toLowerCase();
    if (v === '') return null;
    if (/^[0-4]$/.test(v)) return Number(v);
    const found = PK_ROLES.find((r) => r.name === v);
    return found ? found.level : null;
}

function clampLevel(value: number, fallback: number): number {
    if (!Number.isFinite(value)) return fallback;
    return Math.min(4, Math.max(0, Math.trunc(value)));
}

/**
 * `<pk-role-select>` — the five-level role picker (Reader, Auditor, Member, Operator, Owner), capped at
 * the caller's own level.
 *
 * Renders a native `<select>` in its **light DOM** so it takes part in an enclosing `<form>` with no
 * extra wiring (the posted value is the role **name**, e.g. `member`). Levels above `max-level` are
 * not offered — a caller cannot grant a role above their own. A current value above the cap is shown
 * as a disabled option so the select still reads true.
 *
 * Attributes:
 *   - `name`        — form field name (default `role`).
 *   - `value`       — current role, as a name or a level (`member` or `2`).
 *   - `max-level`   — highest level offered, 0–4 (default 4). Set it to the caller's level.
 *   - `min-level`   — lowest level offered, 0–4 (default 0).
 *   - `label`       — accessible label (default `Role`).
 *   - `disabled`    — boolean; disables the select.
 *   - `auto-submit` — boolean; submits the enclosing form on change (`requestSubmit`).
 *
 * Events:
 *   - `pk-role-change` — `CustomEvent<{ value: string; level: number }>` on change (bubbles, composed).
 */
export class PkRoleSelect extends HTMLElement {
    static readonly tagName = 'pk-role-select';

    static get observedAttributes(): string[] {
        return ['name', 'value', 'max-level', 'min-level', 'label', 'disabled', 'auto-submit'];
    }

    connectedCallback(): void {
        this._render();
    }

    attributeChangedCallback(): void {
        if (this.isConnected) this._render();
    }

    /** The selected role name (`reader`..`owner`). */
    get value(): string {
        const select = this.querySelector('select');
        return select?.value ?? this._currentName();
    }

    set value(v: string) {
        this.setAttribute('value', v);
    }

    private _maxLevel(): number {
        const raw = this.getAttribute('max-level');
        return raw === null ? 4 : clampLevel(Number(raw), 4);
    }

    private _minLevel(): number {
        const raw = this.getAttribute('min-level');
        return raw === null ? 0 : clampLevel(Number(raw), 0);
    }

    private _currentLevel(): number | null {
        return parseRoleLevel(this.getAttribute('value'));
    }

    private _currentName(): string {
        const level = this._currentLevel();
        return level === null ? '' : PK_ROLES[level]!.name;
    }

    private readonly _onChange = (e: Event): void => {
        const select = e.target as HTMLSelectElement;
        const level = parseRoleLevel(select.value) ?? 0;
        this.dispatchEvent(new CustomEvent('pk-role-change', {
            bubbles: true,
            composed: true,
            detail: { value: select.value, level },
        }));
        if (this.hasAttribute('auto-submit')) {
            const form = this.closest('form');
            if (form && typeof form.requestSubmit === 'function') form.requestSubmit();
        }
    };

    private template(): TemplateResult {
        const max = this._maxLevel();
        const min = Math.min(this._minLevel(), max);
        const current = this._currentLevel();
        const offered = PK_ROLES.filter((r) => r.level >= min && r.level <= max);
        const aboveCap = current !== null && (current > max || current < min) ? PK_ROLES[current]! : null;
        const name = this.getAttribute('name') ?? 'role';
        const label = this.getAttribute('label') ?? 'Role';

        return html`
            <select
                class="pk-role-select__select"
                name=${name}
                aria-label=${label}
                ?disabled=${this.hasAttribute('disabled')}
                @change=${this._onChange}
            >
                ${aboveCap ? html`<option value=${aboveCap.name} disabled selected>${aboveCap.label}</option>` : ''}
                ${offered.map((r) => html`
                    <option value=${r.name} ?selected=${current === r.level}>${r.label}</option>
                `)}
            </select>
        `;
    }

    private _render(): void {
        render(this.template(), this);
    }
}

if (!customElements.get(PkRoleSelect.tagName)) {
    customElements.define(PkRoleSelect.tagName, PkRoleSelect);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-role-select': PkRoleSelect;
    }
}
